/**
 * "Puxar do TiFlux" (spec 026, FR-004) — ação de admin na página de um cliente
 * operado no ERP.
 *
 * A PRÉVIA é o estado atual no TiFlux, consultado na hora, ticket a ticket, só
 * dos que têm diferença: comentários e apontamentos novos (entram sempre) e,
 * para etapa, status, responsável e mesa, o valor do TiFlux ao lado do do ERP —
 * com "mudou no ERP desde o desligamento" quando houve, e "mudou no TiFlux"
 * quando o ciclo já tinha gravado a pendência. APLICAR leva só os campos
 * marcados, ticket a ticket; os não marcados ficam como estão no ERP (e a
 * pendência deles sai: foi decidido). Nada é escrito no TiFlux, e o ticket
 * segue fora do espelho.
 *
 * ADR-013 (03/10): o apontamento EDITADO NO ERP que o TiFlux mudou depois da
 * edição aparece à parte, com os dois valores. Marcado, aceita o do TiFlux; não
 * marcado, fica o do ERP — e a pendência sai nos dois casos.
 *
 * `contain: inline-size` na lista (o padrão do ClientPicker, spec 024): título
 * de ticket e trecho de comentário são longos, e dentro do Dialog (grid) o
 * min-content deles alargaria a coluna para fora do diálogo.
 */
import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  CloudDownload,
  Loader2,
  MessageSquare,
  PencilLine,
} from 'lucide-react';
import type {
  TifluxFieldValue,
  TifluxPullApplyBody,
  TifluxPullApplyResult,
  TifluxPullField,
  TifluxPullPreview,
  TifluxPullTicket,
} from '@erp/shared';
import { Badge } from '@/components/ui/misc';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { formatDateTime } from '@/lib/utils';
import { AppointmentValueLines } from '@/features/tickets/appointment-tiflux';
import {
  tifluxErrorMessage,
  tifluxPullFieldLabel,
  tifluxPullPreviewOptions,
  tifluxPullReason,
  tifluxPullWarning,
  useApplyTifluxPull,
} from '@/features/settings/tiflux';

/** marcados por ticket (número LOCAL) */
type Marks = Record<number, TifluxPullField[]>;
/** ADR-013: conflitos de apontamento em que o admin aceita o do TiFlux (por apontamento LOCAL) */
type AppointmentMarks = Record<number, boolean>;

/** o valor do TiFlux não resolveu pelo vínculo: não se decide (nem entra, nem fica) */
const UNRESOLVED = /_(not_linked|missing)$/;

function valueLabel(v: TifluxFieldValue, field: TifluxPullField): string {
  if (v.label) return v.label;
  return field === 'responsible' && v.key === null ? 'sem responsável' : '—';
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/**
 * o pedido: por ticket, os campos que o admin VIU, com o valor de lá e se quer
 * puxá-lo — e os conflitos de apontamento que viu, com a chave do valor de lá
 * e se o aceita (não aceito = fica o do ERP)
 */
export function pullBody(
  preview: TifluxPullPreview,
  marks: Marks,
  appointmentMarks: AppointmentMarks = {}
): TifluxPullApplyBody {
  return {
    tickets: preview.tickets.map((t) => ({
      ticketNumber: t.ticketNumber,
      fields: t.fields
        .filter((f) => !(f.blockedReason && UNRESOLVED.test(f.blockedReason)))
        .map((f) => ({
          field: f.field,
          tifluxKey: f.tiflux.key,
          apply: (marks[t.ticketNumber] ?? []).includes(f.field),
        })),
      appointments: t.appointmentConflicts.map((c) => ({
        appointmentId: c.appointmentId,
        tifluxKey: c.tifluxKey,
        accept: appointmentMarks[c.appointmentId] === true,
      })),
    })),
  };
}

/**
 * Marcar puxa junto o que o campo exige (a mesa leva a etapa; mexer em ticket
 * fechado exige reabrir); desmarcar solta quem dependia dele. É a mesma regra
 * que o servidor confere — a tela só evita o pedido que ele recusaria.
 */
export function toggleMark(
  ticket: TifluxPullTicket,
  current: TifluxPullField[],
  field: TifluxPullField,
  on: boolean
) {
  const byField = new Map(ticket.fields.map((f) => [f.field, f]));
  const set = new Set(current);
  const add = (f: TifluxPullField) => {
    if (set.has(f) || !byField.get(f)?.applicable) return;
    set.add(f);
    for (const r of byField.get(f)?.requires ?? []) add(r);
  };
  const remove = (f: TifluxPullField) => {
    if (!set.has(f)) return;
    set.delete(f);
    for (const [other, diff] of byField) if (diff.requires.includes(f)) remove(other);
  };
  if (on) add(field);
  else remove(field);
  return [...set];
}

export function TifluxPullDialog({
  clientId,
  clientName,
  onClose,
}: {
  clientId: number;
  clientName: string;
  onClose: () => void;
}) {
  const previewQ = useQuery(tifluxPullPreviewOptions(clientId));
  const apply = useApplyTifluxPull(clientId);
  const [marks, setMarks] = useState<Marks>({});
  const [appointmentMarks, setAppointmentMarks] = useState<AppointmentMarks>({});
  const [result, setResult] = useState<TifluxPullApplyResult | null>(null);
  const preview = previewQ.data;
  const markedCount =
    Object.values(marks).reduce((n, fields) => n + fields.length, 0) +
    Object.values(appointmentMarks).filter(Boolean).length;
  const additions = (preview?.tickets ?? []).some(
    (t) => t.comments.length + t.appointments.length > 0
  );

  const again = () => {
    setResult(null);
    setMarks({});
    setAppointmentMarks({});
    apply.reset();
    void previewQ.refetch();
  };

  return (
    <Dialog open onOpenChange={(next) => (next ? null : onClose())}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Puxar do TiFlux — {clientName}</DialogTitle>
          <DialogDescription>
            O estado atual no TiFlux, consultado agora, ticket a ticket. Comentários e apontamentos
            novos entram sempre. Etapa, status, responsável e mesa entram só os que você marcar; os
            não marcados ficam como estão no ERP. Nada é escrito no TiFlux, e os tickets seguem
            operados aqui.
          </DialogDescription>
        </DialogHeader>

        <div className="min-w-0 space-y-3 contain-[inline-size]">
          {result ? (
            <PullResult result={result} preview={preview} />
          ) : previewQ.isFetching && !preview ? (
            <p className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" />
              Consultando o TiFlux — pode levar alguns segundos…
            </p>
          ) : previewQ.isError ? (
            <p className="text-sm text-destructive" role="alert">
              {tifluxErrorMessage(previewQ.error)}
            </p>
          ) : preview ? (
            <PreviewList
              preview={preview}
              marks={marks}
              onToggle={(t, field, on) =>
                setMarks((m) => ({
                  ...m,
                  [t.ticketNumber]: toggleMark(t, m[t.ticketNumber] ?? [], field, on),
                }))
              }
              appointmentMarks={appointmentMarks}
              onToggleAppointment={(appointmentId, on) =>
                setAppointmentMarks((m) => ({ ...m, [appointmentId]: on }))
              }
            />
          ) : null}
          {apply.isError ? (
            <p className="text-sm text-destructive" role="alert">
              {tifluxErrorMessage(apply.error)}
            </p>
          ) : null}
        </div>

        <div className="mt-2 flex flex-wrap items-center justify-end gap-2">
          {result ? (
            <>
              <Button type="button" variant="outline" size="sm" onClick={again}>
                Consultar de novo
              </Button>
              <Button type="button" size="sm" onClick={onClose}>
                Fechar
              </Button>
            </>
          ) : (
            <>
              <Button type="button" variant="outline" size="sm" onClick={onClose}>
                Voltar
              </Button>
              <Button
                type="button"
                size="sm"
                disabled={
                  !preview || preview.tickets.length === 0 || apply.isPending || previewQ.isFetching
                }
                onClick={() => {
                  if (!preview) return;
                  apply.mutate(pullBody(preview, marks, appointmentMarks), { onSuccess: setResult });
                }}
              >
                {apply.isPending ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <CloudDownload className="size-4" />
                )}
                {apply.isPending
                  ? 'Aplicando…'
                  : markedCount > 0
                    ? `Aplicar (${plural(markedCount, 'item marcado', 'itens marcados')})`
                    : additions
                      ? 'Aplicar comentários e apontamentos'
                      : 'Manter o ERP como está'}
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PreviewList({
  preview,
  marks,
  onToggle,
  appointmentMarks,
  onToggleAppointment,
}: {
  preview: TifluxPullPreview;
  marks: Marks;
  onToggle: (t: TifluxPullTicket, field: TifluxPullField, on: boolean) => void;
  appointmentMarks: AppointmentMarks;
  onToggleAppointment: (appointmentId: number, on: boolean) => void;
}) {
  return (
    <div className="space-y-3">
      {preview.warnings.length > 0 || preview.unchecked.length > 0 ? (
        <div className="space-y-1 rounded-md border border-[#FFE58F] bg-[#FFFBE6] px-3 py-2 text-xs text-[#D48806]">
          {preview.warnings.map((w) => (
            <p key={w} className="flex items-start gap-1.5">
              <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
              {tifluxPullWarning(w)}
            </p>
          ))}
          {preview.unchecked.length > 0 ? (
            <p className="flex items-start gap-1.5">
              <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
              Não deu tempo de conferir comentários e apontamentos de{' '}
              {preview.unchecked.map((n) => `#${n}`).join(', ')} — consulte de novo em instantes (o
              ciclo automático também os traz).
            </p>
          ) : null}
        </div>
      ) : null}

      {preview.tickets.length === 0 ? (
        <p className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
          <CheckCircle2 className="size-4 text-[#52C41A]" />
          Nada diferente no TiFlux: os tickets deste cliente batem com o ERP.
        </p>
      ) : (
        <ul className="space-y-3">
          {preview.tickets.map((t) => (
            <PreviewTicket
              key={t.ticketNumber}
              ticket={t}
              marked={marks[t.ticketNumber] ?? []}
              onToggle={onToggle}
              appointmentMarks={appointmentMarks}
              onToggleAppointment={onToggleAppointment}
            />
          ))}
        </ul>
      )}
      <p className="text-xs text-muted-foreground">
        Consultado em {formatDateTime(preview.checkedAt)}.
      </p>
    </div>
  );
}

function PreviewTicket({
  ticket,
  marked,
  onToggle,
  appointmentMarks,
  onToggleAppointment,
}: {
  ticket: TifluxPullTicket;
  marked: TifluxPullField[];
  onToggle: (t: TifluxPullTicket, field: TifluxPullField, on: boolean) => void;
  appointmentMarks: AppointmentMarks;
  onToggleAppointment: (appointmentId: number, on: boolean) => void;
}) {
  return (
    <li className="min-w-0 space-y-2 rounded-md border px-3 py-2">
      <div className="flex min-w-0 items-baseline gap-2">
        <Link
          to="/tickets/$ticketNumber"
          params={{ ticketNumber: String(ticket.ticketNumber) }}
          className="shrink-0 text-sm font-semibold text-primary hover:underline"
        >
          #{ticket.ticketNumber}
        </Link>
        <span className="min-w-0 truncate text-sm" title={ticket.title}>
          {ticket.title}
        </span>
        <span className="ml-auto shrink-0 font-mono text-xs text-muted-foreground">
          TiFlux #{ticket.tifluxTicketNumber}
        </span>
      </div>

      {ticket.fields.length > 0 ? (
        <ul className="space-y-1.5">
          {ticket.fields.map((f) => {
            const id = `pull-${ticket.ticketNumber}-${f.field}`;
            return (
              <li
                key={f.field}
                className="flex min-w-0 items-start gap-2 rounded-md bg-muted/40 px-2 py-1.5"
              >
                <Checkbox
                  id={id}
                  className="mt-0.5"
                  checked={marked.includes(f.field)}
                  disabled={!f.applicable}
                  onCheckedChange={(v) => onToggle(ticket, f.field, v === true)}
                  aria-label={`Puxar ${tifluxPullFieldLabel(f.field)} do TiFlux`}
                />
                <div className="min-w-0 flex-1 space-y-0.5 text-sm">
                  <label htmlFor={id} className="font-medium">
                    {tifluxPullFieldLabel(f.field)}
                  </label>
                  <div className="grid min-w-0 gap-x-4 text-xs sm:grid-cols-2">
                    <span className="min-w-0 truncate" title={valueLabel(f.tiflux, f.field)}>
                      TiFlux: <strong>{valueLabel(f.tiflux, f.field)}</strong>
                    </span>
                    <span
                      className="min-w-0 truncate text-muted-foreground"
                      title={valueLabel(f.erp, f.field)}
                    >
                      ERP: {valueLabel(f.erp, f.field)}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-1">
                    {f.pendingSince ? (
                      <Badge variant="warning">
                        mudou no TiFlux · visto {formatDateTime(f.pendingSince)}
                      </Badge>
                    ) : null}
                    {f.erpChangedSinceDetach ? (
                      <Badge variant="info">mudou no ERP desde o desligamento</Badge>
                    ) : null}
                    {f.applicable && f.requires.length > 0 ? (
                      <span className="text-xs text-muted-foreground">
                        marca junto:{' '}
                        {f.requires.map((r) => tifluxPullFieldLabel(r).toLowerCase()).join(', ')}
                      </span>
                    ) : null}
                    {!f.applicable ? (
                      <span className="text-xs text-muted-foreground">
                        {tifluxPullReason(f.blockedReason)}
                      </span>
                    ) : null}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      ) : null}

      {ticket.appointmentConflicts.length > 0 ? (
        <div className="space-y-1.5">
          <p className="flex items-center gap-1.5 text-xs font-medium">
            <PencilLine className="size-3.5 text-muted-foreground" />
            {plural(
              ticket.appointmentConflicts.length,
              'apontamento editado no ERP mudou no TiFlux',
              'apontamentos editados no ERP mudaram no TiFlux'
            )}{' '}
            — marque para aceitar o do TiFlux; sem marca, fica o do ERP
          </p>
          <ul className="space-y-1.5">
            {ticket.appointmentConflicts.map((c) => {
              const id = `pull-appt-${c.appointmentId}`;
              return (
                <li
                  key={c.appointmentId}
                  className="flex min-w-0 items-start gap-2 rounded-md bg-muted/40 px-2 py-1.5"
                >
                  <Checkbox
                    id={id}
                    className="mt-0.5"
                    checked={appointmentMarks[c.appointmentId] === true}
                    onCheckedChange={(v) => onToggleAppointment(c.appointmentId, v === true)}
                    aria-label="Aceitar o apontamento do TiFlux"
                  />
                  <div className="min-w-0 flex-1 space-y-0.5 text-sm">
                    <label htmlFor={id} className="font-medium">
                      Aceitar o do TiFlux
                    </label>
                    <AppointmentValueLines side="TiFlux" value={c.tiflux} strong />
                    <AppointmentValueLines side="ERP" value={c.erp} />
                    <div className="flex flex-wrap items-center gap-1">
                      {c.erpEditedAt ? (
                        <Badge variant="info">
                          editado no ERP{c.erpEditedBy ? ` por ${c.erpEditedBy}` : ''} ·{' '}
                          {formatDateTime(c.erpEditedAt)}
                        </Badge>
                      ) : null}
                      {c.pendingSince ? (
                        <Badge variant="warning">
                          mudou no TiFlux · visto {formatDateTime(c.pendingSince)}
                        </Badge>
                      ) : null}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}

      {ticket.comments.length > 0 ? (
        <div className="space-y-1">
          <p className="flex items-center gap-1.5 text-xs font-medium">
            <MessageSquare className="size-3.5 text-muted-foreground" />
            {plural(ticket.comments.length, 'comentário novo', 'comentários novos')} — entram
          </p>
          <ul className="space-y-0.5 pl-5 text-xs text-muted-foreground">
            {ticket.comments.map((c) => (
              <li key={c.tifluxId} className="line-clamp-2 min-w-0 break-words">
                <span className="font-medium text-foreground">{c.author ?? 'TiFlux'}</span>
                {c.createdAt ? ` · ${formatDateTime(c.createdAt)}` : ''} — {c.excerpt}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {ticket.appointments.length > 0 ? (
        <div className="space-y-1">
          <p className="flex items-center gap-1.5 text-xs font-medium">
            <Clock className="size-3.5 text-muted-foreground" />
            {plural(ticket.appointments.length, 'apontamento', 'apontamentos')} — entram
          </p>
          <ul className="space-y-0.5 pl-5 text-xs text-muted-foreground">
            {ticket.appointments.map((a) => (
              <li key={a.tifluxId} className="min-w-0 truncate">
                <span className="font-medium text-foreground">{a.user ?? 'TiFlux'}</span> ·{' '}
                {formatDateTime(a.startedAt)}–
                {new Date(a.endedAt).toLocaleTimeString('pt-BR', { timeStyle: 'short' })}
                {a.description ? ` · ${a.description}` : ''}
                {a.change === 'changed' ? ' (atualizado)' : ''}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </li>
  );
}

function PullResult({
  result,
  preview,
}: {
  result: TifluxPullApplyResult;
  preview: TifluxPullPreview | undefined;
}) {
  const titleOf = (n: number) => preview?.tickets.find((t) => t.ticketNumber === n)?.title ?? '';
  return (
    <ul className="space-y-2">
      {result.tickets.map((r) => {
        const additions = [
          r.comments > 0 ? plural(r.comments, 'comentário', 'comentários') : null,
          r.appointments.inserted + r.appointments.updated > 0
            ? plural(
                r.appointments.inserted + r.appointments.updated,
                'apontamento',
                'apontamentos'
              )
            : null,
        ].filter(Boolean);
        return (
          <li
            key={r.ticketNumber}
            className="min-w-0 space-y-0.5 rounded-md border px-3 py-2 text-sm"
          >
            <p className="flex min-w-0 items-baseline gap-2">
              <span className="shrink-0 font-semibold">#{r.ticketNumber}</span>
              <span className="min-w-0 truncate text-muted-foreground">
                {titleOf(r.ticketNumber)}
              </span>
            </p>
            {r.error ? (
              <p className="text-xs text-destructive">
                Não processado: {tifluxPullReason(r.error)}
              </p>
            ) : null}
            {r.applied.map((a) => (
              <p key={a.field} className="text-xs">
                <CheckCircle2 className="mr-1 inline size-3.5 text-[#52C41A]" />
                {tifluxPullFieldLabel(a.field)}: {a.from ?? '—'} → {a.to ?? '—'}
              </p>
            ))}
            {r.kept.length > 0 ? (
              <p className="text-xs text-muted-foreground">
                Mantido no ERP:{' '}
                {r.kept.map((f) => tifluxPullFieldLabel(f).toLowerCase()).join(', ')}
              </p>
            ) : null}
            {r.stale.length > 0 ? (
              <p className="text-xs text-[#D48806]">
                Mudou de novo no TiFlux, não aplicado (consulte de novo):{' '}
                {r.stale.map((f) => tifluxPullFieldLabel(f).toLowerCase()).join(', ')}
              </p>
            ) : null}
            {r.failed.map((f) => (
              <p key={f.field} className="text-xs text-destructive">
                {tifluxPullFieldLabel(f.field)} não aplicado: {tifluxPullReason(f.error)}
              </p>
            ))}
            {additions.length > 0 ? (
              <p className="text-xs text-muted-foreground">Entraram: {additions.join(', ')}.</p>
            ) : null}
            {r.appointments.accepted > 0 ? (
              <p className="text-xs">
                <CheckCircle2 className="mr-1 inline size-3.5 text-[#52C41A]" />
                {plural(r.appointments.accepted, 'apontamento aceito', 'apontamentos aceitos')} do
                TiFlux
              </p>
            ) : null}
            {r.appointments.kept > 0 ? (
              <p className="text-xs text-muted-foreground">
                Mantido no ERP:{' '}
                {plural(r.appointments.kept, 'apontamento editado', 'apontamentos editados')}
              </p>
            ) : null}
            {r.appointments.stale > 0 ? (
              <p className="text-xs text-[#D48806]">
                {plural(r.appointments.stale, 'apontamento mudou', 'apontamentos mudaram')} de novo
                no TiFlux, nada decidido (consulte de novo)
              </p>
            ) : null}
            {!r.error &&
            r.applied.length + r.kept.length + r.stale.length + r.failed.length === 0 &&
            r.appointments.accepted + r.appointments.kept + r.appointments.stale === 0 &&
            additions.length === 0 ? (
              <p className="text-xs text-muted-foreground">Nada a aplicar.</p>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}
